import { AnalysisBlockProps, AnalysisResultBlockProps } from './AnalysisBlock/AnalysisBlock'
import { getSafeFactorScore } from './scoreUtils'
const FLOW_ANALYSIS_TERMINOLOGY_PREFIX = 'dashboard.experience.projectDetailPage.flowAnalysis.terminology'

function genTrend(score: number, meanScore: number): 'up' | 'down' | 'normal' {
  const floorScore = Math.floor(score)
  const floorMeanScore = Math.floor(meanScore)
  const RANGE = 4
  if (floorScore >= floorMeanScore + RANGE) {
    return 'up'
  } else if (floorScore <= floorMeanScore - RANGE) {
    return 'down'
  } else {
    return 'normal'
  }
}

function createIndicator(i18nKey: string, factorScore?: Partial<ProjectModels.FactorScore>) {
  const safeFactorScore = getSafeFactorScore(factorScore)
  return {
    i18nKey,
    index: safeFactorScore.displayScore,
    norm: safeFactorScore.displayMeanScore,
    trend: safeFactorScore.isComparable && safeFactorScore.score !== undefined && safeFactorScore.meanScore !== undefined
      ? genTrend(safeFactorScore.score, safeFactorScore.meanScore)
      : undefined
  }
}


/************************************ 热力图分析提示 ************************************/
export const HEATMAP_TIPS: AnalysisBlockProps[] = [
  {
    title: 'AdEff is conducting frame-by-frame analysis to identify video content.',
    content: [],
    status: 'completed'
  },
  {
    title: 'Video frame recognition is complete.',
    content: [],
    status: 'completed'
  },
  {
    title: "Analyzing the audience's visual focus of attention.",
    content: [
      { title: "Conducting an analysis of the audience's visual attention regions in accordance with target audience attributes.", status: 'completed' },
      { title: "Audience visual analysis is complete, and the heatmap is being processed.", status: 'completed' },
    ],
    status: 'completed'
  },
]

export const HEATMAP_MEDIA: AnalysisResultBlockProps = {
  title: 'The dynamic heatmap has been generated.',
  type: 'media',
  content: null
}

/************************************ 主观反应曲线分析提示 ************************************/

export const SUBJECTIVE_RESPONSE_CURVE_TIPS: AnalysisBlockProps[] = [
  {
    title: "Analyzing the audience's psychological reactions to watching ads.",
    content: [
      { title: "Performing real-time second-by-second analysis on the audience's attentional, emotional and cognitive responses during ad exposure.", status: 'completed' },
      { title: "Audience reaction analysis is complete, and the subjective response curve is being constructed.", status: 'completed' },
    ],
    status: 'completed'
  },
]

export const SUBJECTIVE_RESPONSE_CURVE_MEDIA: AnalysisResultBlockProps = {
  title: "The subjective response curve has been generated.",
  type: 'media',
  content: null
}

/************************************ 指标分析提示 ************************************/

export const INDEX_ANALYSES_TIPS: AnalysisBlockProps[] = [
  {
    title: "Analyzing audience reactions at key moments",
    content: [
      { title: "Evaluating psychological responses during the golden first 3 seconds, information moments, branding moments, interest peaks, and final 3 seconds.", status: 'completed' },
      { title: "Moment analysis completed. Summarizing moment metrics.", status: 'completed' },
    ],
    status: 'completed'
  },
]

export const INDEX_ANALYSES_MEDIA: AnalysisResultBlockProps = {
  title: "Moment performance generated",
  type: 'media',
  content: null
}

export const genIndexAnalysisIndicators = (dimensionFactors: ProjectModels.DimensionFactors) => {
  const { attentionForce, emotionalForce, messageForce, brandForce } = dimensionFactors || {}
  return [
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.gazeRecognition`, attentionForce?.gazeRecognition),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.emotionRecognition`, emotionalForce?.emotionRecognition),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.engagementRecognition`, messageForce?.engagementRecognition),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.theGolden3Seconds`, attentionForce?.golden3sPerformance),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.keyMessage`, messageForce?.keyMessagePerformance),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.brand`, brandForce?.brandPerformance),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.interest`, emotionalForce?.interestPeakPerformance),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.ending3Seconds`, attentionForce?.ending3sPerformance)
  ].filter((indicator) => {
    const shouldHideMissingMoment =
      indicator.index === -1 &&
      [
        `${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.keyMessage`,
        `${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.brand`,
        `${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.interest`,
      ].includes(indicator.i18nKey)
    return !shouldHideMissingMoment
  })
}

/************************************ 消费者态度分析提示 ************************************/

export const CONSUMER_ATTITUDE_ANALYSIS_TIPS: AnalysisBlockProps[] = [
  {
    title: "Analyzing consumers' attitudes.",
    content: [
      { title: "The system is analyzing consumers' attitudinal inclinations toward the brand, products and content.", status: 'completed' },
      { title: "Consumer attitude analysis is complete, and core indicators are being summarized.", status: 'completed' },
    ],
    status: 'completed'
  },
]

export const CONSUMER_ATTITUDE_ANALYSIS_MEDIA: AnalysisResultBlockProps = {
  title: "Consumer attitudes have been generated.",
  type: 'media',
  content: null
}

export const genConsumerAttitudeAnalysisIndicators = (dimensionFactors: ProjectModels.DimensionFactors) => {
  const { emotionalForce, messageForce, brandForce, persuasiveForce } = dimensionFactors || {}
  return [
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.liking`, emotionalForce?.liking),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.newInformation`, messageForce?.newInformation),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.messageRelevance`, messageForce?.messageRelevance),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.comprehension`, messageForce?.comprehension),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.credibility`, messageForce?.credibility),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.brandLinkage`, brandForce?.brandLinkage),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.uniqueness`, brandForce?.uniqueness),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.willing`, persuasiveForce?.willingnessToLearnMore),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.purchase`, persuasiveForce?.purchaseIntention),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.share`, persuasiveForce?.shareability),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.brandLikeLift`, persuasiveForce?.brandLikeabilityLift),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.attitudePart.purchaseIntentLift`, persuasiveForce?.purchaseIntentLift),
  ]
}


/************************************ 五大维度分析提示 ************************************/

export const FIVE_DEMENSION_BENEFITS_TIPS: AnalysisBlockProps[] = [
  {
    title: "Generating five force drive scores",
    content: [],
    status: 'completed'
  },
]

export const FIVE_DEMENSION_BENEFITS_MEDIA: AnalysisResultBlockProps = {
  title: "Five force drive scores ready",
  type: 'media',
  content: null
}

export const genFiveDimensionBenefitsIndicators = (dimensionFactors: ProjectModels.DimensionFactors) => {
  const { attentionForce: attensionForce, emotionalForce, messageForce, brandForce, persuasiveForce } = dimensionFactors || {}
  return [
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.dimensionPart.attentionForce`, attensionForce?.overall),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.dimensionPart.emotionalForce`, emotionalForce?.overall),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.dimensionPart.messageForce`, messageForce?.overall),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.dimensionPart.brandForce`, brandForce?.overall),
    createIndicator(`${FLOW_ANALYSIS_TERMINOLOGY_PREFIX}.dimensionPart.persuasiveForce`, persuasiveForce?.overall),
  ]
}

/************************************ 核心结果分析提示 ************************************/

export const FINAL_RESULT_MEDIA: AnalysisResultBlockProps = {
  title: "All analyses are complete – check out your ad performance now!",
  type: 'end',
  content: null
}
